import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/utils";

export const badgeSurfaceColors = {
  destructive: "text-badge-destructive",
  warning: "text-yellow-700/10",
  outline: "text-border",
};

// Helper function to convert hex color to rgba with opacity
function hexToRgba(hex: string, opacity: number): string {
  const sanitized = hex.replace("#", "");
  const r = parseInt(sanitized.substring(0, 2), 16);
  const g = parseInt(sanitized.substring(2, 4), 16);
  const b = parseInt(sanitized.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const badgeVariants = cva(
  "inline-flex items-center rounded-md border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      size: {
        default: "px-1.5 py-1 text-xs h-6",
        small: "px-1 py-0.5 text-xs h-5 rounded-sm",
      },
      variant: {
        default:
          "border-transparent bg-gradient-to-b from-[#3B3B3E]/80 to-[#3B3B3E] text-primary-foreground shadow hover:from-[#2B2B2E]/80 hover:to-[#2B2B2E]",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-badge-destructive text-red-700 hover:bg-red-700/20",
        warning:
          "border-transparent bg-yellow-700/10 text-yellow-700 hover:bg-yellow-700/20",
        amber:
          "border-transparent bg-amber-600/10 text-amber-700 hover:bg-amber-600/20",
        outline: "text-foreground",
        success:
          "border-transparent bg-green-700/10 text-green-700 hover:bg-green-700/20",
        info: "border-transparent bg-blue-50 text-blue-700 hover:bg-blue-700/20",
        infoDark:
          "border-transparent bg-blue-500/15 text-blue-200 shadow-none hover:bg-blue-500/20",
        successDark:
          "border-emerald-400/0 bg-emerald-400/15 text-emerald-200 shadow-none hover:bg-emerald-400/20",
        custom: "border-[var(--badge-color)] bg-[color:var(--badge-bg)]",
        dashed:
          "bg-transparent border-dashed border-muted-foreground/40 text-muted-foreground/80",
        dashedDark:
          "border-dashed border-white/60 bg-white/10 text-white/70 shadow-none",
        opportunity:
          "h-5 justify-center overflow-hidden rounded-[6px] border-[#f8cc9a] bg-orange-100 px-[5px] py-px text-sm font-normal text-orange-800",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  color?: string;
  size?: "default" | "small";
  mode?: "light" | "dark";
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    { className, variant, color, size = "default", mode = "light", ...props },
    ref,
  ) => {
    const finalVariant = color ? "custom" : variant;
    const resolvedVariant =
      mode === "dark" && !color
        ? finalVariant === "info"
          ? "infoDark"
          : finalVariant === "success"
            ? "successDark"
            : finalVariant === "dashed"
              ? "dashedDark"
              : finalVariant
        : finalVariant;

    // If className contains a solid bg-* (not gradient), add bg-none to remove any gradient
    const hasSolidBg =
      className && /\bbg-(?!gradient|none|auto)[\w-]+/.test(className);

    const bgImage = hasSolidBg ? "bg-none" : "";

    return (
      <div
        className={cn(
          badgeVariants({ variant: resolvedVariant, size }),
          resolvedVariant === "successDark" ||
            resolvedVariant === "dashedDark" ||
            resolvedVariant === "opportunity"
            ? ""
            : "rounded-sm",
          className,
          bgImage,
        )}
        ref={ref}
        style={
          color
            ? ({
                "--badge-color": color,
                "--badge-bg": hexToRgba(color, 0.2),
              } as React.CSSProperties)
            : undefined
        }
        {...props}
      />
    );
  },
);
Badge.displayName = "Badge";
